import { useState, Fragment } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, CalendarCheck, Send, ChevronDown, ChevronRight, Phone } from "lucide-react";
import { LogoMark, Reveal, RevealLines, ContactLinks } from "./shared";
import { NAV, CONTACT, ACKNOWLEDGEMENT, TAGLINE, SERVICE_MENU, TEAM_MENU } from "../content";
import { EnquiryBtn } from "./enquiry";

export const Caps = ({ children, className = "" }) => <p className={`flex items-center gap-3 font-jakarta text-xs uppercase tracking-[0.3em] text-[#5E6B57] ${className}`}><span aria-hidden className="w-6 h-px bg-[#C59B45] shrink-0" />{children}</p>;

export const GoldRule = ({ className = "" }) => <span aria-hidden className={`block h-px w-20 bg-gradient-to-r from-[#C59B45] to-[#C59B45]/0 ${className}`} />;

export const Wave = ({ className = "" }) => (
  <svg aria-hidden viewBox="0 0 1440 220" preserveAspectRatio="none" className={`block w-full h-28 sm:h-40 lg:h-48 ${className}`} data-testid="wave-divider">
    <path d="M0,120 C180,60 360,70 520,120 C700,176 900,150 1080,90 C1220,44 1340,40 1440,60 L1440,220 L0,220 Z" fill="#A8D1BC" fillOpacity="0.55" />
    <path d="M0,150 C200,96 380,110 560,150 C760,196 960,168 1140,112 C1270,72 1370,78 1440,92 L1440,220 L0,220 Z" fill="#1E7356" />
    <path d="M0,150 C200,96 380,110 560,150 C760,196 960,168 1140,112 C1270,72 1370,78 1440,92" fill="none" stroke="#C9A24A" strokeWidth="2.2" />
    <path d="M0,184 C240,140 420,150 640,186 C860,216 1060,190 1240,150 C1330,130 1400,128 1440,132 L1440,220 L0,220 Z" className="fill-current text-[#3F4A3C]" />
    <path d="M0,184 C240,140 420,150 640,186 C860,216 1060,190 1240,150 C1330,130 1400,128 1440,132" fill="none" stroke="#C9A24A" strokeWidth="1.6" />
    <path d="M760,210 C900,176 1060,160 1220,122 C1320,100 1400,96 1440,100" fill="none" stroke="#C9A24A" strokeWidth="1" strokeOpacity="0.8" />
  </svg>
);

const Leaf = ({ src = "corner", className = "" }) => <img src={`/images/leaf-merch-${src}.png`} alt="" aria-hidden loading="lazy" className={`pointer-events-none select-none absolute ${className}`} />;
export { Leaf };
export const H2 = ({ children, className = "" }) => <h2 className={`font-newsreader text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#3F4A3C] ${className}`}>{children}</h2>;

export const BookBtn = ({ className = "", testid = "book-online-btn", label = "Book online" }) => (
  <Link to="/book" data-testid={testid} className={`group inline-flex items-center gap-2 rounded-full bg-[#3F4A3C] px-6 py-3.5 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors duration-500 ${className}`}>
    <CalendarCheck className="w-4 h-4" /> {label}
  </Link>
);
export const ReferBtn = ({ className = "", testid = "refer-btn" }) => (
  <Link to="/refer" data-testid={testid} className={`inline-flex items-center gap-2 rounded-full border border-[#3F4A3C]/25 px-6 py-3.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors duration-500 ${className}`}>
    <Send className="w-4 h-4" /> Make a referral
  </Link>
);

const tabCls = ({ isActive }) => `py-1 border-b whitespace-nowrap transition-colors ${isActive ? "border-[#D9A85A] text-[#3F4A3C]" : "border-transparent hover:text-[#3F4A3C]"}`;

const itemId = (s) => s.to.split(/[#/]/).pop();
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const FlyoutItem = ({ s }) => (
  <div className="relative group/sub" data-testid={`nav-services-flyout-${itemId(s)}`}>
    <Link to={s.to} aria-haspopup="true" data-testid={`nav-services-item-${itemId(s)}`} className="flex items-center justify-between rounded-xl px-4 py-2.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] group-hover/sub:bg-[#E3E9E4] transition-colors">
      {s.label}<ChevronRight className="w-3.5 h-3.5 text-[#C59B45] transition-transform duration-300 group-hover/sub:translate-x-0.5" />
    </Link>
    <div className="absolute left-full top-0 pl-3 opacity-0 invisible -translate-x-1 group-hover/sub:opacity-100 group-hover/sub:visible group-hover/sub:translate-x-0 group-focus-within/sub:opacity-100 group-focus-within/sub:visible transition-[opacity,transform] duration-300">
      <div className="w-64 rounded-2xl bg-[#FBF9F4] border border-[#3F4A3C]/10 shadow-[0_24px_60px_-30px_rgba(63,74,60,0.45)] p-2" data-testid={`nav-services-submenu-${itemId(s)}`}>
        <p className="px-4 pt-2 pb-1 font-jakarta text-[11px] uppercase tracking-[0.2em] text-[#8A6A2E]">{s.label}</p>
        {s.sub.map((x) => <Link key={x.to} to={x.to} data-testid={`nav-services-sub-${slug(x.label)}`} className="flex items-center gap-2 rounded-xl px-4 py-2.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors"><span className="w-3 h-px bg-[#C59B45]" />{x.label}</Link>)}
      </div>
    </div>
  </div>
);

const MENUS = { "/services": SERVICE_MENU, "/team": TEAM_MENU };

const MenuSection = ({ label, testid }) => (
  <div className="mt-2 pt-3 border-t border-[#3F4A3C]/10 px-4 pb-1 font-jakarta text-[11px] uppercase tracking-[0.2em] text-[#8A6A2E]" data-testid={testid}>{label}</div>
);

const DropdownTab = ({ n, menu, k }) => (
  <div className="relative group" data-testid={`nav-${k}-dropdown`}>
    <NavLink to={n.to} aria-haspopup="true" data-testid={`nav-${k}`} className={(s) => `${tabCls(s)} inline-flex items-center gap-1`}>{n.label}<ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" /></NavLink>
    <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-[opacity,transform] duration-300">
      <div className="w-72 rounded-2xl bg-[#FBF9F4] border border-[#3F4A3C]/10 shadow-[0_24px_60px_-30px_rgba(63,74,60,0.45)] p-2" data-testid={`nav-${k}-menu`}>
        {menu.map((s) => <Fragment key={s.to}>{s.section && <MenuSection label={s.section} testid={`nav-${k}-section-${slug(s.section)}`} />}{s.sub ? <FlyoutItem s={s} /> : (
          <Link to={s.to} data-testid={`nav-${k}-item-${itemId(s)}`} className="block rounded-xl px-4 py-2.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors">{s.label}</Link>
        )}</Fragment>)}
      </div>
    </div>
  </div>
);

const NavTabs = ({ suffix = "" }) => NAV.map((n) => MENUS[n.to] ? <DropdownTab key={n.to} n={n} menu={MENUS[n.to]} k={n.to.slice(1)} /> : (
  <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}${suffix}`} className={tabCls}>{n.label}</NavLink>
));

const MobileSubGroup = ({ s, close, item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`mobile-services-group-${itemId(s)}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`mobile-services-toggle-${itemId(s)}`}
        className="w-full flex items-center justify-between rounded-2xl px-4 py-3 font-jakarta text-[15px] text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors">
        {s.label}<ChevronDown className={`w-4 h-4 text-[#C59B45] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="grid gap-1 pb-1" data-testid={`mobile-services-sublist-${itemId(s)}`}>{s.sub.map((x) => item(x, true))}</div>}
    </div>
  );
};

const MobileServices = ({ n, close, menu, k }) => {
  const [open, setOpen] = useState(false);
  const item = (s, sub) => (
    <Link key={s.to} to={s.to} onClick={close} data-testid={sub ? `mobile-${k}-sub-${slug(s.label)}` : `mobile-${k}-item-${itemId(s)}`}
      className={`flex items-center justify-between rounded-2xl px-4 py-3 font-jakarta text-[15px] text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors ${sub ? "ml-5 text-sm text-[#5E6B57]" : ""}`}>
      <span className="flex items-center gap-2">{sub && <span className="w-3 h-px bg-[#C59B45]" />}{s.label}</span><ArrowUpRight className="w-4 h-4 opacity-50" />
    </Link>
  );
  return (
    <div className="border-b border-[#3F4A3C]/10">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`mobile-nav-${k}`} className="w-full flex items-center justify-between py-4 font-newsreader text-2xl text-[#3F4A3C]">
        {n.label}<ChevronDown className={`w-5 h-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 grid gap-1 rounded-3xl bg-[#FBF9F4] p-2 mb-4" data-testid={`mobile-${k}-list`}>{menu.map((s) => <Fragment key={s.to}>{s.section && <MenuSection label={s.section} testid={`mobile-${k}-section-${slug(s.section)}`} />}{s.sub ? <MobileSubGroup s={s} close={close} item={item} /> : item(s)}</Fragment>)}</div>}
    </div>
  );
};

export const SiteNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#F6F2E9]/90 backdrop-blur-xl border-b border-[#3F4A3C]/10" data-testid="site-nav">
      <div className="w-full px-5 sm:px-10 lg:px-12 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <LogoMark className="w-11 h-11" />
          <span className="leading-none"><span className="block font-newsreader text-lg tracking-[0.12em] text-[#3F4A3C]">INNER YOU</span><span className="block font-jakarta text-[9px] tracking-[0.38em] text-[#5E6B57] mt-1">DEVELOPMENT</span></span>
        </Link>
        <div className="hidden md:flex items-center gap-2">
          <a href={CONTACT.phoneHref} data-testid="nav-call" className="hidden lg:inline-flex whitespace-nowrap items-center gap-2 rounded-full border border-[#3F4A3C]/20 px-5 py-2.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors"><Phone className="w-4 h-4" />Call our practice</a>
          <EnquiryBtn testid="nav-enquiry" label="Enquire" className="!py-2.5 !px-5" />
          <BookBtn testid="nav-book" className="!py-2.5" />
        </div>
        <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menu" className="lg:hidden w-11 h-11 grid place-items-center rounded-full bg-[#E3E9E4] text-[#3F4A3C]">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
      </div>
      <nav className="hidden lg:flex w-full px-5 sm:px-10 lg:px-12 h-12 items-center justify-between font-jakarta text-sm text-[#4A4F45] border-t border-[#3F4A3C]/10" data-testid="nav-tabs-row">
        <NavTabs />
      </nav>
      {open && (
        <div className="lg:hidden border-t border-[#3F4A3C]/10 bg-[#F6F2E9] px-5 pb-6 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-menu">
          {NAV.map((n) => MENUS[n.to] ? <MobileServices key={n.to} n={n} menu={MENUS[n.to]} k={n.to.slice(1)} close={() => setOpen(false)} /> : (
            <div key={n.to} className="border-b border-[#3F4A3C]/10">
              <Link to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`} className="block py-4 font-newsreader text-2xl text-[#3F4A3C]">{n.label}</Link>
            </div>
          ))}
          <div className="mt-6 flex flex-wrap gap-3"><BookBtn testid="mobile-book" /><EnquiryBtn testid="mobile-enquiry" /></div>
        </div>
      )}
    </header>
  );
};

export const PageHero = ({ eyebrow, lines, sub, children }) => (
  <section className="relative pt-16 sm:pt-24 pb-14 sm:pb-20 overflow-hidden" data-testid="page-hero">
    <img src="/images/leaf-merch-corner.png" alt="" aria-hidden className="pointer-events-none select-none absolute top-4 right-3 sm:right-8 hidden sm:block w-52 lg:w-72 opacity-95" />
    <img src="/images/leaf-merch-low.png" alt="" aria-hidden className="hidden md:block pointer-events-none select-none absolute right-0 bottom-0 w-80 lg:w-[25rem] -scale-x-100 opacity-80" />
    <div className="relative max-w-[1360px] mx-auto px-5 sm:px-10">
      <Caps>{eyebrow}</Caps>
      <RevealLines lines={lines} emIndex={lines.length - 1} emClass="italic text-[#5F7C8A]" className="mt-6 font-newsreader text-[#3F4A3C] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl" />
      {sub && <Reveal delay={0.5} className="mt-8 max-w-2xl"><span className="block w-14 h-px bg-[#C59B45] mb-6" /><p className="font-jakarta text-base sm:text-lg leading-relaxed text-[#4A4F45]">{sub}</p></Reveal>}
      {children && <Reveal delay={0.6} className="mt-8 flex flex-wrap gap-3">{children}</Reveal>}
    </div>
  </section>
);

export const CtaBand = () => (
  <section className="px-4 sm:px-8 py-20" data-testid="cta-band">
    <div className="relative overflow-hidden max-w-[1360px] mx-auto rounded-[36px] bg-[#3F4A3C] text-[#F6F2E9] px-6 sm:px-14 pt-14 pb-20 grid lg:grid-cols-2 gap-8 items-center">
      <svg aria-hidden viewBox="0 0 1440 80" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 w-full h-12 sm:h-16"><path d="M0,50 C300,10 520,80 820,44 C1080,14 1250,60 1440,30 L1440,80 L0,80 Z" fill="#ffffff" fillOpacity="0.07" /><path d="M0,50 C300,10 520,80 820,44 C1080,14 1250,60 1440,30" fill="none" stroke="#C59B45" strokeWidth="2" /></svg>
      <div className="relative"><p className="font-script text-3xl text-[#F3DCA8]">A soft landing</p><h2 className="mt-2 font-newsreader text-3xl sm:text-5xl leading-tight">Not sure where to start? We'll help you work it out.</h2></div>
      <div className="relative flex flex-wrap gap-3 lg:justify-end">
        <BookBtn testid="cta-book" className="!bg-[#F6F2E9] !text-[#3F4A3C] hover:!bg-white" />
        <EnquiryBtn testid="cta-enquiry" className="!border-white/40 !text-[#F6F2E9] hover:!bg-white/10" />
        <Link to="/refer" data-testid="cta-refer" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-jakarta text-sm hover:bg-white/10 transition-colors">Make a referral <ArrowUpRight className="w-4 h-4" /></Link>
        <Link to="/newsletter" data-testid="cta-newsletter" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-jakarta text-sm hover:bg-white/10 transition-colors">Subscribe to our newsletter <ArrowUpRight className="w-4 h-4" /></Link>
        <a href={CONTACT.phoneHref} data-testid="cta-call" className="inline-flex items-center rounded-full border border-white/40 px-6 py-3.5 font-jakarta text-sm hover:bg-white/10 transition-colors">Give us a call</a>
      </div>
    </div>
  </section>
);

export const Acknowledgement = () => (
  <section className="relative overflow-hidden bg-[#F9F6EF] border-t border-[#3F4A3C]/10 pt-20 sm:pt-24 pb-6" data-testid="acknowledgement-section">
    <Reveal className="relative max-w-3xl mx-auto px-6 text-center">
      <p className="font-jakarta text-xs uppercase tracking-[0.32em] text-[#5E6B57]">Acknowledgement of Country</p>
      <span className="block w-14 h-px bg-[#C59B45] mx-auto my-8" />
      <p className="font-newsreader italic text-2xl sm:text-3xl lg:text-[2.1rem] leading-relaxed text-[#3F4A3C]" data-testid="acknowledgement">{ACKNOWLEDGEMENT}</p>
    </Reveal>
  </section>
);

export const SiteFooter = ({ ack = false }) => (
  <>
  {ack && <Acknowledgement />}
  <div className="relative bg-[#F9F6EF]" data-testid="footer-wave">
    <Leaf src="low" className="z-10 left-0 bottom-2 w-36 sm:w-72 lg:w-[22rem]" />
    <Leaf src="corner" className="z-10 right-6 sm:right-16 bottom-12 sm:bottom-20 w-16 sm:w-36 lg:w-44 rotate-[10deg]" />
    <Wave className="relative -mb-px" />
  </div>
  <footer className="bg-[#3F4A3C] text-[#F6F2E9] pt-16 pb-28" data-testid="site-footer">
    <div className="max-w-[1360px] mx-auto px-5 sm:px-10 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-6">
        <div className="flex items-center gap-3"><LogoMark className="w-12 h-12" /><span className="font-newsreader text-2xl">Inner You Development</span></div>
        <p className="mt-4 font-jakarta text-xs uppercase tracking-[0.3em] text-[#D9DFCF]">{TAGLINE}</p>
        <ContactLinks prefix="footer" className="mt-8 flex flex-col gap-3 font-jakarta text-sm sm:text-base" linkClass="hover:text-[#F3DCA8] transition-colors" />
        <p className="mt-3 font-jakarta text-sm text-[#D9DFCF]">{CONTACT.address}</p>
      </div>
      <div className="md:col-span-6 flex flex-wrap content-start gap-x-6 gap-y-3 font-jakarta text-sm" data-testid="footer-tabs">
        {NAV.map((n) => <Link key={n.to} to={n.to} data-testid={`footer-nav-${n.to.slice(1)}`} className="text-[#EEEADF] hover:text-[#F3DCA8] transition-colors">{n.label}</Link>)}
        <Link to="/refer" data-testid="footer-nav-refer" className="text-[#EEEADF] hover:text-[#F3DCA8]">Make a referral</Link>
      </div>
    </div>
    <p className="max-w-[1360px] mx-auto px-5 sm:px-10 mt-14 font-jakarta text-xs text-[#D9DFCF]">© {new Date().getFullYear()} Inner You Development Pty Ltd</p>
  </footer>
  </>
);

export const SiteLayout = ({ children, testid, ack = false }) => (
  <div className="bg-[#F6F2E9] text-[#4A4F45] overflow-x-hidden min-h-screen" data-testid={testid}>
    <SiteNav />
    <main>{children}</main>
    <SiteFooter ack={ack} />
  </div>
);
